import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, ListChecks, Sparkle, Crown, Books, BookOpen, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";

const MATERIAL_SNIPPETS = {
    default: [
        { title: "Foundations & syllabus overview", body: "Understand the exam blueprint, weightage, and the type of thinking each section rewards." },
        { title: "Topic-wise formula sheets & concept maps", body: "Compact revision notes with worked examples for each topic." },
        { title: "Previous years' pattern analysis", body: "Frequency of concepts, difficulty trend, and time-per-question benchmarks." },
        { title: "Strategy playbook", body: "How to sequence sections, allocate time, and use elimination to maximise score." },
    ],
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function CourseDetailPage() {
    const { courseId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [testType, setTestType] = useState("practice");
    const [topic, setTopic] = useState("all");
    const [numQ, setNumQ] = useState(15);
    const [difficulty, setDifficulty] = useState("medium");
    const [loading, setLoading] = useState(false);
    const [chapterQuery, setChapterQuery] = useState("");

    useEffect(() => {
        api.get(`/courses/${courseId}`).then(r => setCourse(r.data)).catch(() => nav("/courses"));
    }, [courseId, nav]);

    const start = async () => {
        if (!user) { nav("/login"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/tests/generate", {
                course_id: courseId,
                test_type: testType,
                topic: topic === "all" ? null : topic,
                num_questions: Number(numQ),
                difficulty,
            });
            nav(`/tests/${data.id}`, { state: { test: data } });
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.error("Premium required for more mock tests");
                nav("/pricing");
            } else {
                toast.error(err?.response?.data?.detail || "Could not generate test");
            }
        } finally { setLoading(false); }
    };

    if (!course) {
        return <div className="min-h-screen grid place-items-center"><div className="dot-pulse"></div></div>;
    }

    return (
        <div className="min-h-screen" data-testid="course-detail-page">
            <Navbar />
            <div className="h-3" style={{ backgroundColor: course.color }}></div>
            <section className="mx-auto max-w-7xl px-6 py-12 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                    <div className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-3">{course.category}</div>
                    <h1 className="font-serif text-5xl md:text-6xl text-ink leading-none">{course.name}</h1>
                    <p className="text-lg text-ink/70 mt-3">{course.full_name}</p>

                    <div className="mt-8 flex flex-wrap gap-6 text-sm">
                        <div className="flex items-center gap-2 text-ink/70"><Clock size={16}/> {course.duration_min} min exam</div>
                        <div className="flex items-center gap-2 text-ink/70"><ListChecks size={16}/> {course.questions} questions</div>
                    </div>

                    <div className="mt-12">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-4">
                            <Books size={16} weight="duotone"/> Study material
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {MATERIAL_SNIPPETS.default.map((m) => (
                                <div key={m.title} className="p-5 border border-ink/15 rounded-sm bg-bone" data-testid={`material-${m.title.replace(/\s+/g,'-').toLowerCase()}`}>
                                    <div className="font-serif text-lg text-ink">{m.title}</div>
                                    <div className="text-sm text-ink/60 mt-1">{m.body}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-12">
                        <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
                            <div>
                                <div className="text-xs uppercase tracking-[0.3em] text-gold">Complete syllabus</div>
                                <div className="font-serif text-2xl text-ink mt-1">
                                    {course.subjects?.length || 0} subjects · {course.subjects?.reduce((n, s) => n + s.chapters.length, 0) || 0} chapters
                                </div>
                            </div>
                            <div className="relative w-full sm:w-72">
                                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"/>
                                <Input value={chapterQuery} onChange={e => setChapterQuery(e.target.value)} placeholder="Filter chapters…" className="pl-8 h-9 rounded-sm border-ink/20 text-sm" data-testid="input-chapter-search"/>
                            </div>
                        </div>

                        <div className="space-y-8">
                            {(course.subjects || []).map((subj) => {
                                const filteredChapters = subj.chapters.filter(c =>
                                    !chapterQuery || c.toLowerCase().includes(chapterQuery.toLowerCase())
                                );
                                if (filteredChapters.length === 0 && chapterQuery) return null;
                                const totalW = (course.subjects || []).reduce((n, s) => n + (s.weight || 0), 0);
                                const pct = totalW > 0 && subj.weight ? Math.round(subj.weight * 100 / totalW) : null;
                                return (
                                    <div key={subj.name} data-testid={`subject-${slug(subj.name)}`}>
                                        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                                            <div className="flex items-baseline gap-3 flex-wrap">
                                                <h3 className="font-serif text-2xl text-ink">{subj.name}</h3>
                                                {subj.weight != null && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 bg-gold/10 border border-gold/40 text-gold-dark rounded-sm" data-testid={`weight-${slug(subj.name)}`}>
                                                        {subj.weight} Qs{pct != null && ` · ${pct}%`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs uppercase tracking-widest text-ink/50">{filteredChapters.length} chapter{filteredChapters.length !== 1 ? "s" : ""}</div>
                                        </div>
                                        {subj.weight != null && (
                                            <div className="mb-3 h-1 bg-ink/10 rounded-sm overflow-hidden">
                                                <div className="h-full bg-gold" style={{ width: `${pct}%` }}></div>
                                            </div>
                                        )}
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {filteredChapters.map(chapter => (
                                                <Link key={chapter}
                                                      to={`/courses/${course.id}/lesson?topic=${encodeURIComponent(chapter)}`}
                                                      data-testid={`chapter-${slug(chapter)}`}
                                                      className="group flex items-center justify-between px-3 py-2.5 border border-ink/10 rounded-sm bg-bone hover:border-ink/40 hover:bg-parchment/40 transition-colors duration-150">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <BookOpen size={14} weight="duotone" className="text-ink/70 shrink-0"/>
                                                        <span className="text-sm text-ink truncate">{chapter}</span>
                                                    </div>
                                                    <ArrowRight size={12} className="text-ink/30 group-hover:text-ink group-hover:translate-x-0.5 transition-all shrink-0 ml-2"/>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Right rail — start a test */}
                <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
                    <div className="border border-ink/15 rounded-sm bg-bone p-8" data-testid="start-test-panel">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-ink/50 mb-1">
                            <Sparkle size={14} weight="fill" className="text-gold"/> AI-generated
                        </div>
                        <div className="font-serif text-2xl text-ink">Start a test</div>
                        <div className="text-sm text-ink/60 mt-1">Choose the flavour and we'll draft a fresh paper for you.</div>

                        <div className="mt-6 space-y-5">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-ink/60">Type</Label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button data-testid="btn-type-practice" onClick={() => setTestType("practice")} className={`p-3 border rounded-sm text-left transition-colors duration-150 ${testType === "practice" ? "border-ink bg-ink text-bone" : "border-ink/20 bg-bone text-ink hover:border-ink/50"}`}>
                                        <div className="font-serif text-lg">Practice</div>
                                        <div className="text-[11px] opacity-70">Quick · Free</div>
                                    </button>
                                    <button data-testid="btn-type-mock" onClick={() => setTestType("mock")} className={`p-3 border rounded-sm text-left transition-colors duration-150 ${testType === "mock" ? "border-ink bg-ink text-bone" : "border-ink/20 bg-bone text-ink hover:border-ink/50"}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="font-serif text-lg">Full Mock</div>
                                            {!user?.is_premium && <Crown size={14} className="text-gold"/>}
                                        </div>
                                        <div className="text-[11px] opacity-70">Timed · Exam pace</div>
                                    </button>
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest text-ink/60">Topic</Label>
                                <Select value={topic} onValueChange={setTopic}>
                                    <SelectTrigger className="mt-2 rounded-sm" data-testid="select-topic"><SelectValue/></SelectTrigger>
                                    <SelectContent className="max-h-96">
                                        <SelectItem value="all">All topics (mixed)</SelectItem>
                                        {(course.subjects || []).map(subj => (
                                            <SelectGroup key={subj.name}>
                                                <SelectLabel className="text-[10px] uppercase tracking-widest text-gold-dark pt-2">{subj.name}</SelectLabel>
                                                <SelectItem value={subj.name}>All {subj.name}</SelectItem>
                                                {subj.chapters.map(ch => (
                                                    <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                                                ))}
                                            </SelectGroup>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-ink/60">Difficulty</Label>
                                    <Select value={difficulty} onValueChange={setDifficulty}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="select-difficulty"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="easy">Easy</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="hard">Hard</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-ink/60">Questions</Label>
                                    <Select value={String(numQ)} onValueChange={v => setNumQ(Number(v))}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="select-numq"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="5">5</SelectItem>
                                            <SelectItem value="10">10</SelectItem>
                                            <SelectItem value="15">15</SelectItem>
                                            <SelectItem value="20">20</SelectItem>
                                            <SelectItem value="25">25</SelectItem>
                                            <SelectItem value="30">30</SelectItem>
                                            <SelectItem value="40">40</SelectItem>
                                            {testType === "mock" && <SelectItem value="50">50</SelectItem>}
                                            {testType === "mock" && <SelectItem value="75">75</SelectItem>}
                                            {testType === "mock" && <SelectItem value="100">100</SelectItem>}
                                            {testType !== "mock" && <SelectItem value="50">50</SelectItem>}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <Button onClick={start} disabled={loading} className="w-full bg-ink text-bone hover:bg-ink/90 rounded-sm h-12" data-testid="btn-start-test">
                                {loading ? "Generating your paper…" : `Begin ${testType === "mock" ? "mock exam" : "practice test"}`}
                            </Button>
                            {loading && <div className="flex items-center gap-2 justify-center text-xs text-ink/50"><div className="dot-pulse"></div> Curating {numQ} questions with AI</div>}
                        </div>
                    </div>
                </aside>
            </section>
            <Footer />
        </div>
    );
}
